import React, { useState, useCallback } from 'react';
import { File, CloudArrowUp, Trash } from '@phosphor-icons/react';
import { useDocuments } from '../../../hooks/useDocuments';
import { useApp } from '../../../contexts/AppContext';
import { LoadingSpinner } from '../../../utils/LoadingSpinner';
import modalStyles from '../../../styles/modals.module.css';
import styles from './BudgetPage.module.css';

const DocumentsModal = ({
  onClose,
  onSubmitRef,
  documents = [],
  projectId,
  recordId,
  recordType = 'financial',
  onDocumentsChange, // Add callback for document changes
}) => {
  const [dragActive, setDragActive] = useState(false);
  const { showSuccess, showError } = useApp();

  // Use the new documents hook
  const { uploadDocument, deleteDocument, isUploading, uploadProgress, error } = useDocuments({
    projectId,
    recordId,
    recordType,
  });

  // Set up submit ref for ModalManager's Enter key handling
  React.useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(() => {
        // No submit action needed for this modal
        onClose();
      });
    }
  }, [onSubmitRef, onClose]);

  const handleUpload = useCallback(
    async files => {
      for (const file of files) {
        try {
          const newDoc = await uploadDocument(file);

          // Update documents state with new document
          if (onDocumentsChange) {
            const updatedDocs = [...documents, newDoc];
            onDocumentsChange(updatedDocs);
          }

          showSuccess(`המסמך ${file.name} הועלה בהצלחה`);
          onClose();
        } catch (err) {
          showError('שגיאה בהעלאת הקובץ');
        }
      }
    },
    [uploadDocument, onClose, documents, onDocumentsChange],
  );

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setDragActive(false);
      const files = [...e.dataTransfer.files];
      handleUpload(files);
    },
    [handleUpload],
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const handleFileSelect = useCallback(
    e => {
      const files = [...e.target.files];
      handleUpload(files);
    },
    [handleUpload],
  );

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>מסמכים</h2>
        </div>

        <div className={modalStyles.content}>
          {/* Existing Documents */}
          {documents.length > 0 && (
            <div className={modalStyles.section}>
              <h3 className={modalStyles.sectionTitle}>מסמכים קיימים</h3>
              <div className={styles.docsList}>
                {documents.map((doc, index) => (
                  <div key={doc.path || index} className={styles.docItem}>
                    <div className={styles.docItemLeft}>
                      <File size={16} />
                      <a
                        href={doc.url}
                        className={styles.documentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.name}
                      </a>
                    </div>
                    <button
                      className={styles.deleteButton}
                      onClick={async () => {
                        try {
                          const result = await deleteDocument(doc.path);
                          // If deletion was successful, update local state
                          if (result && onDocumentsChange) {
                            // Remove the deleted document from the array
                            const updatedDocs = documents.filter(d => d.path !== doc.path);
                            onDocumentsChange(updatedDocs);
                            showSuccess(`המסמך ${doc.name} נמחק בהצלחה`); // Show success message
                            onClose(); // Close the modal
                          }
                        } catch (err) {
                          showError('שגיאה במחיקת המסמך');
                        }
                      }}
                      type="button"
                      aria-label="Delete document"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Area */}
          <div className={modalStyles.section}>
            <h3 className={modalStyles.sectionTitle}>העלאת מסמכים חדשים</h3>
            <div
              className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                className={styles.fileInput}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                disabled={isUploading}
              />

              <div className={styles.uploadMessage}>
                {isUploading ? (
                  <div className={styles.uploadProgress}>
                    <LoadingSpinner size="small" />
                    <span>מעלה קובץ... {Math.round(uploadProgress)}%</span>
                  </div>
                ) : (
                  <>
                    <CloudArrowUp size={32} />
                    <span>גרור קבצים לכאן או לחץ לבחירה</span>
                    <div className={styles.uploadHint}>ניתן להעלות קבצים עד 25MB</div>
                  </>
                )}
              </div>
            </div>

            {error && <div className={modalStyles.error}>{error}</div>}
          </div>
        </div>

        <div className={modalStyles.footerEnd}>
          <button type="button" className={modalStyles.secondaryButton} onClick={onClose}>
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentsModal;
